import { Box, IconButton, List, ListItem, ListItemProps, ListItemText, SxProps, Typography } from "@mui/material";
import { ReactElement, ReactNode, useState } from "react";
import colors from "../../colors";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { CabDropdown } from "@CabComponents/CabDropdown";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { CabAutocomplete } from "@CabComponents/CabAutocomplete";
import { CabIcon } from "@CabComponents/CabIcon";
import { BsDot } from "react-icons/bs";
import { IoAdd, IoOpenOutline, IoTrashOutline, IoReorderTwo } from "react-icons/io5";
import { Link } from "react-router-dom";
import { CabButton } from "@CabComponents/CabButton";
import { useFieldArray, useForm } from "react-hook-form";
import { validateEmail } from "../../utils/inputUtils";
import { FormController } from "../../utils/formUtils";
import { CabTimezoneInput } from "@CabComponents/CabTimezoneInput";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface WidgetProps {
  header?: string;
  action?: ReactNode;
  children: ReactNode;
  sx?: SxProps;
}

export const Widget = ({ header, action, children, sx }: WidgetProps): ReactElement => (
  <Box
    sx={[{
      border: `1px solid ${colors.black400}`,
      borderRadius: 2,
      padding: 1,
      paddingTop: 0,
      paddingBottom: 0,
      width: '100%',
      overflow: "hidden"
    }, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}>
      {header && (
        <Typography
          variant="h2"
          sx={{
            fontSize: 18,
            paddingTop: 2,
            paddingLeft: 1,
            paddingBottom: 2
          }}>
          {header}
        </Typography>
      )}
      {action}
    </Box>
    {children}
  </Box>
);

export const WidgetList = ({ children }: { children: ReactNode }) => (
  <List disablePadding sx={{ paddingLeft: 1, paddingRight: 1 }}>
    {children}
  </List>
);

export const WidgetListItem = ({ onClick, onBlur, secondaryAction, density, children, divider = true, sx }: {
  onClick?: () => void;
  onBlur?: () => void;
  secondaryAction?: ListItemProps['secondaryAction'];
  density?: "standard" | "comfortable";
  children: ReactNode;
  divider?: boolean;
  sx?: SxProps;
}) => (
  <ListItem
    divider={divider}
    disableGutters
    secondaryAction={secondaryAction}
    onClick={onClick}
    onBlur={onBlur}
    sx={{ 
      cursor: onClick ? 'pointer' : undefined, 
      paddingTop: 0,
      paddingBottom: density === "comfortable" ? "4px" : 0,
      ...sx,
    }}
  >
    {children}
  </ListItem>
);

export interface BasicInfoListItemProps {
  title: string;
  text?: string | null;
  inputType: 'text'|'select'|'multi-select'|'multiline'|'autocomplete'|'list'|'timezone';
  inputValue: string | number | number[] | string[] | null;
  onSave: (val: string | number | number[] | string[] | (string | number)[] | null) => void;
  color?: string; 
  selectOptions?: { value: string|number, label: string, color?: string }[];
  onInputChange?: (val: string) => void; 
  placeholder?: string; 
  noOptionsText?: string;
  onClick?: () => void;
  isDirty?: boolean;
  link?: string;
  newTab?: boolean;
  error?: string;
  sortId?: string | number;
  draggable?: boolean;
  isEditingOrder?: boolean;
  readOnly?: boolean;
}

export const BasicInfoListItem = ({ 
  title, text, color, inputType, inputValue, selectOptions, onSave, onInputChange, placeholder, noOptionsText, onClick,
  isDirty, link, newTab, error, sortId, draggable, isEditingOrder, readOnly
}: BasicInfoListItemProps): ReactElement => {
  const [editing, setEditing] = useState(false);

  const {
    setNodeRef,
    attributes,
    listeners,
    transition,
    transform
  } = useSortable({ id: sortId || title });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleItemClick = () => {
    if (!editing && !isEditingOrder && !readOnly) {
      setEditing(true);
    }
  };

  const handleItemBlur = () => {
    setEditing(false);
  };

  const handleEnter: React.KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditing(false);
    }
  };

  const content = (
    <>
      <WidgetListItem
        onClick={readOnly ? undefined : handleItemClick}
        divider={false}
        sx={!editing ? {
          '&:hover': {
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: colors.black300,
            }
          },
          position: 'relative',
        } : undefined}
        secondaryAction={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
            {isDirty ? <CabIcon Icon={BsDot} size="large" color={colors.black400} sx={{ marginTop: 3 }} />
              : (link && !editing && (
                <Link to={link} onClick={e => e.stopPropagation()} target={newTab ? '_blank' : undefined}>
                  <CabIcon Icon={IoOpenOutline} sx={{ marginTop: 3 }} />
                </Link>
              ))
            }
          </Box>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            width: "100%"
          }}>
          {draggable && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                marginTop: 1
              }}>
              <CabIcon Icon={IoReorderTwo} size="small" color={colors.black400} />
            </Box>
          )}
          <Box sx={{
            flex: 1
          }}>
            <ListItemText
              primary={title}
              secondary={(
                <Box>
                  <Box
                    sx={{
                      visibility: editing ? 'visible' : 'hidden',
                      height: editing ? undefined : 0
                    }}>
                    {inputType === 'text' ? (
                      <CabTextInput
                        onBlur={handleItemBlur}
                        onKeyDown={handleEnter}
                        overrides={{ autoFocus: true }}
                        value={inputValue}
                        onChange={e => onSave(e.target.value)}
                        fullWidth
                      />
                    ) : inputType === 'multiline' ? (
                      <CabTextInput
                        onBlur={handleItemBlur}
                        onKeyDown={handleEnter}
                        overrides={{ autoFocus: true }}
                        value={inputValue}
                        onChange={e => onSave(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={2}
                      />
                    ) : inputType === 'autocomplete' ? (
                      <CabAutocomplete
                        overrides={{ autoFocus: true, onBlur: handleItemBlur }}
                        onKeyDown={handleEnter}
                        options={selectOptions?.map(option => ({
                          value: option.value,
                          label: option.label
                        })) || []}
                        noOptionsText={noOptionsText}
                        placeholder={placeholder}
                        onInputChange={onInputChange ? (v) => onInputChange(v) : undefined}
                        value={typeof inputValue !== 'string' ? inputValue : null}
                        onChange={onSave}
                        sx={{ width: '100%' }}
                      />
                    ) : inputType === 'select' || inputType === 'multi-select' ? (
                      <CabDropdown
                        multiple={inputType === 'multi-select'}
                        overrides={{
                          autoFocus: true,
                          onKeyDown: handleEnter,
                          onBlur: handleItemBlur,
                        }}
                        options={selectOptions?.map(option => ({
                          value: option.value,
                          label: option.color ? <CabToggleChip chipColor={option.color} label={option.label} selected />
                            : option.label
                        })) || []}
                        value={
                          inputType === 'multi-select'
                            ? (Array.isArray(inputValue) ? inputValue : [])
                            : (inputValue ?? '')
                        }
                        onChange={e => {
                          if (inputType === 'multi-select') {
                            // MUI Select with multiple returns an array directly
                            const value = Array.isArray(e.target.value) ? e.target.value : [];
                            onSave(value);
                          } else {
                            onSave(e.target.value);
                          }
                        }}
                        placeholder={inputType === 'multi-select' ? (placeholder || "Select options...") : undefined}
                        sx={{ width: '100%' }}
                      />
                    ) : inputType === 'timezone' ? (
                      <CabTimezoneInput
                        value={String(inputValue)}
                        onChange={onSave}
                        overrides={{ autoFocus: true }}
                      />
                    ) : (
                      Array.isArray(inputValue) && inputValue.every(v => typeof v === 'string') && (
                        <ListInput value={inputValue} onChange={onSave} onDone={handleItemBlur} />
                      )
                    )}
                  </Box>
                  {!editing && (
                    <Box>
                      {inputType === 'multi-select' ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            marginTop: 0.5
                          }}>
                          {Array.isArray(inputValue) && inputValue.length > 0 ? (
                            inputValue.map((val, idx) => {
                              const option = selectOptions?.find(opt => {
                                // Compare values - handle both string and number types
                                if (typeof opt.value === 'number' && typeof val === 'number') {
                                  return opt.value === val;
                                }
                                const optValue = typeof opt.value === 'string' ? opt.value : String(opt.value);
                                const valStr = typeof val === 'string' ? val : String(val);
                                return optValue === valStr;
                              });
                              return (
                                <CabToggleChip
                                  key={idx}
                                  chipColor={option?.color || color || colors.black300}
                                  label={
                                    typeof option?.label === 'string' 
                                      ? option.label 
                                      : String(option?.label || val)
                                  }
                                  selected
                                />
                              );
                            })
                          ) : (
                            <Typography sx={{
                              fontSize: 13
                            }}>--</Typography>
                          )}
                        </Box>
                      ) : color ? (
                        <CabToggleChip chipColor={color} label={text || '--'} selected />
                      ) : (
                        <Typography sx={{
                          fontSize: 13
                        }}>
                          {text && text.trim() !== '' ? text : '--'}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              slotProps={{
                primary: {
                  sx: { fontSize: 12, fontWeight: 'bolder', color: colors.black700, fontFamily: 'Inter' }
                },

                secondary: editing
                  ? { component: Box }
                  : {
                    onClick,
                    sx: {
                      cursor: onClick ? 'pointer' : undefined,
                      fontWeight: 'bold',
                      fontSize: 16,
                      color: colors.black900,
                    }
                  }
              }} />
          </Box>
        </Box>
      </WidgetListItem>
      {error && (
        <Typography variant="body2" color="error" sx={{
          fontStyle: "italic"
        }}>{error}</Typography>
      )}
    </>
  );

  return (
    <>
      {draggable ? (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
          {content}
        </div>
      ) : (
        content
      )}
    </>
  );
};

const ListInput = ({ value, onChange, onDone }: {
  value: string[];
  onChange: (v: string[]) => void;
  onDone: () => void;
}) => {
  const { control, handleSubmit } = useForm({ defaultValues: { emails: value.map(v => ({ value: v })) } });
  const { fields, append, remove } = useFieldArray({ control, name: 'emails' });

  const handleDone = (values: { emails: { value: string }[] }) => {
    onChange(values.emails.map(v => v.value));
    onDone();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1
      }}>
      {fields.map((e, idx) => (
        <FormController
          rules={{
            required: true,
            validate: (v) => validateEmail(v.value) ? true : 'Invalid email'
          }}
          key={e.id}
          name={`emails.${idx}`}
          control={control}
          render={({ field: { ref, ...field }}) => (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center"
              }}>
              <CabTextInput
                inputRef={ref}
                size="xsmall"
                formControlSx={{ flex: 1 }}
                value={field.value.value}
                onChange={ev => field.onChange({ value: ev.target.value })}
                overrides={{ autoFocus: idx === fields.length - 1 && fields.length > 1 }}
              />
              <IconButton edge='end' aria-label='remove' disableRipple onClick={() => remove(idx)}>
                <CabIcon Icon={IoTrashOutline} alt='Remove' sx={{color: colors.black900 }} />
              </IconButton>
            </Box>
          )}
        />
      ))}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 1
        }}>
        <CabButton
          icon={<CabIcon Icon={IoAdd} />}
          buttonType="tertiary"
          size="small"
          sx={{ flex: 1 }}
          onClick={() => append({ value: '' })}
        >
          Add New
        </CabButton>
        <CabButton size="small" sx={{ flex: 1 }} onClick={handleSubmit(handleDone)}>
          Done
        </CabButton>
      </Box>
    </Box>
  );
};
